// The text, integer, decimal or date control in a custom field's drawer. Text and number conditions are
// rounded pills; date conditions are rows naming the dates each covers today. Below them are the box (or
// boxes) the condition needs and Add. Add or Enter applies; the boxes then clear. A value typed in but not
// added is applied when the drawer or the panel closes, through the draft registry the panel provides.
import React, {useContext, useEffect, useRef, useState} from 'react';
import {useIntl} from 'react-intl';
import Styled from 'styled-components';
import {isEqual} from 'lodash';
import moment from 'moment';
import {ACTION_BUTTON_SIZE, ActionButton, Button, DateRangePicker, EMPHASIS, TextField} from '@forecast-it/design-system';
import {formatCustomFieldValue, type CustomFieldColumnDef} from '../custom-field-columns/customFieldColumnModel';
import type {ValueCondition} from './customFieldFilterConditions';
import {isDatePreset, resolveDatePreset} from './customFieldFilterDates';
import {CustomFieldDraftContext} from './customFieldFilterDrafts';
import {
	EMPTY_DRAFT,
	conditionOpsFor,
	needsInput,
	nextSelection,
	numberFormatError,
	opOf,
	toCondition,
	type ConditionDraft,
	type ConditionOp,
} from './customFieldFilterInput';
import {customFieldFilterKey} from './customFieldFilterKeys';
import {CONDITION_MESSAGE_IDS, RANGE_INVALID_MESSAGE_ID, type FormatMessage} from './customFieldFilterLabels';
import {
	MenuRow,
	Muted,
	NEGATIVE_TEXT,
	PANEL_ACCENT,
	PANEL_FONT_SIZE,
	PILL_BORDER,
	PILL_TEXT,
	isPressKey,
} from './customFieldFilter.styled';

const INTEGER_CHARACTERS = '^-?\\d*$';
const DECIMAL_CHARACTERS = '^[-\\d.,]*$';

const Pills = Styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 6px;
	padding: 10px 12px 4px;
`;

// Conditions are compact design-system action buttons, fully rounded as pills; the selected one is filled
// with the panel's accent.
const PillSlot = Styled.span<{selected: boolean}>`
	display: inline-flex;

	& button {
		height: 26px;
		padding: 0 10px;
		border-radius: 13px;
		border: 1px solid ${PILL_BORDER};
		background-color: #fff;
		color: ${PILL_TEXT};
		font-weight: 400;
	}

	& button:hover {
		border-color: ${PANEL_ACCENT};
	}

	${({selected}) =>
		selected &&
		`
		& button,
		& button:hover {
			background-color: ${PANEL_ACCENT};
			border-color: ${PANEL_ACCENT};
			color: #fff;
		}
	`}
`;

const DateOptions = Styled.div`
	padding: 4px 0 6px;
`;

const Body = Styled.div`
	display: flex;
	flex-direction: column;
	gap: 6px;
	padding: 8px 12px 12px;
`;

const InputRow = Styled.div`
	display: flex;
	align-items: flex-start;
	gap: 8px;
	font-size: ${PANEL_FONT_SIZE};
`;

const Grow = Styled.div`
	flex: 1 1 auto;
	min-width: 0;
`;

const NumberBox = Styled.div`
	width: 110px;
`;

// The word between the two number boxes, centred on the boxes rather than on their error text.
const And = Styled(Muted)`
	line-height: 32px;
`;

// Add sits beside the design system's small text field, so it takes that field's height.
const AddSlot = Styled.div`
	& button {
		height: 32px;
		min-width: 0;
	}
`;

const Helper = Styled(Muted)`
	font-size: ${PANEL_FONT_SIZE};
	line-height: 1.3;
`;

const RangeError = Styled.span`
	font-size: ${PANEL_FONT_SIZE};
	color: ${NEGATIVE_TEXT};
`;

const TEXT_PLACEHOLDER_IDS: Partial<Record<ConditionOp, string>> = {
	contains: 'filters.custom_field.text_placeholder.contains',
	is: 'filters.custom_field.text_placeholder.is_exactly',
};

export interface CustomFieldConditionFilterProps {
	definition: CustomFieldColumnDef;
	applied: ValueCondition[];
	decimalSeparator: ',' | '.';
	onApply: (values: ValueCondition[]) => void;
	/** The viewer's locale, for the dates beside each date condition. */
	locale?: string;
}

// The dates a preset covers today, in the viewer's format: one date, or "from – to".
const presetHint = (
	definition: CustomFieldColumnDef,
	op: ConditionOp,
	locale: string,
	formatMessage: FormatMessage
): string | null => {
	if (!isDatePreset(op)) return null;
	const {from, to} = resolveDatePreset(op, moment());
	const show = (iso: string): string => formatCustomFieldValue(definition, iso, locale);
	return from === to
		? show(from)
		: formatMessage({id: 'filters.custom_field.date_hint_range'}, {from: show(from), to: show(to)});
};

export const CustomFieldConditionFilter = ({
	definition,
	applied,
	decimalSeparator,
	onApply,
	locale = 'en',
}: CustomFieldConditionFilterProps): JSX.Element => {
	const intl = useIntl();
	const say: FormatMessage = (descriptor, values) => intl.formatMessage(descriptor, values) as string;
	const filterType = customFieldFilterKey(definition.id);
	const cy = `cf-filter-${definition.key}`;
	const [op, setOp] = useState<ConditionOp>(() => opOf(applied[0] ?? null, definition.kind));
	const [draft, setDraft] = useState<ConditionDraft>(EMPTY_DRAFT);
	const result = toCondition(definition.kind, op, draft, decimalSeparator);
	const next = 'condition' in result ? nextSelection(definition.kind, applied, result.condition) : null;
	const canAdd = next !== null && !isEqual(next, applied);

	// Only a typed value is applied by closing; a pill alone never is.
	const latestTyped = useRef<ValueCondition[] | null>(null);
	latestTyped.current = canAdd && needsInput(op) ? next : null;
	const registry = useContext(CustomFieldDraftContext);
	useEffect(() => registry?.register(filterType, () => latestTyped.current), [registry, filterType]);

	const apply = () => {
		if (!canAdd || !next) return;
		onApply(next);
		setDraft(EMPTY_DRAFT);
	};
	const applyOnEnter = (event: React.KeyboardEvent) => {
		if (event.key !== 'Enter') return;
		event.preventDefault();
		apply();
	};

	const isNumber = definition.kind === 'INTEGER' || definition.kind === 'DECIMAL';
	const formatErrorText = (error: 'integer' | 'decimal' | undefined): string | undefined =>
		error &&
		intl.formatMessage({
			id:
				error === 'integer'
					? 'opportunities.inline_edit.custom_field_integer_invalid'
					: 'opportunities.inline_edit.custom_field_decimal_invalid',
		});
	const rangeErrorText =
		'error' in result && result.error === 'range'
			? intl.formatMessage({id: RANGE_INVALID_MESSAGE_ID, defaultMessage: 'From must not be greater than To'})
			: undefined;
	const errorText =
		rangeErrorText ?? ('error' in result ? formatErrorText(result.error as 'integer' | 'decimal') : undefined);
	const boxError = (part: keyof ConditionDraft): string | undefined => {
		if (op === 'between') {
			const own = formatErrorText(numberFormatError(definition.kind, draft[part], decimalSeparator));
			return part === 'to' ? own ?? rangeErrorText : own;
		}
		return errorText;
	};
	const box = (part: keyof ConditionDraft, placeholderId: string, showError: boolean) => {
		const placeholder = say({id: placeholderId});
		return (
			<TextField
				data-cy={`${cy}-${part}`}
				aria-label={placeholder}
				placeholder={placeholder}
				size="small"
				width="flex"
				value={draft[part]}
				onValueChange={(value: string) => setDraft({...draft, [part]: value})}
				allowedCharactersRegex={
					definition.kind === 'INTEGER' ? INTEGER_CHARACTERS : isNumber ? DECIMAL_CHARACTERS : undefined
				}
				errorText={showError ? boxError(part) : undefined}
			/>
		);
	};
	const addButton = (
		<AddSlot>
			<Button emphasis={EMPHASIS.MEDIUM} noMargin data-cy={`${cy}-add`} disabled={!canAdd} onPress={apply}>
				{say({id: 'common.add'})}
			</Button>
		</AddSlot>
	);
	const conditionName = (id: ConditionOp): string => say({id: CONDITION_MESSAGE_IDS[id]});
	const groupLabel = say({id: 'filters.custom_field.condition'});

	if (definition.kind === 'DATE') {
		return (
			<div data-cy={cy}>
				<DateOptions role="group" aria-label={groupLabel}>
					{conditionOpsFor(definition.kind).map(id => {
						const hint = presetHint(definition, id, locale, say);
						return (
							<MenuRow
								key={id}
								role="button"
								tabIndex={0}
								data-cy={`${cy}-op-${id}`}
								aria-pressed={id === op}
								selected={id === op}
								onClick={() => setOp(id)}
								onKeyDown={(event: React.KeyboardEvent) => {
									if (!isPressKey(event.key)) return;
									event.preventDefault();
									setOp(id);
								}}
							>
								<span>{conditionName(id)}</span>
								{hint && <Muted>{hint}</Muted>}
							</MenuRow>
						);
					})}
				</DateOptions>
				<Body>
					{op === 'custom_range' && (
						<DateRangePicker
							data-cy={`${cy}-dates`}
							aria-label={conditionName('custom_range')}
							width="flex"
							startDate={draft.from || undefined}
							endDate={draft.to || undefined}
							onChange={(range: {startDate?: string; endDate?: string}) =>
								setDraft({...draft, from: range.startDate ?? '', to: range.endDate ?? ''})
							}
						/>
					)}
					{op === 'custom_range' && errorText && <RangeError role="alert">{errorText}</RangeError>}
					<InputRow>{addButton}</InputRow>
				</Body>
			</div>
		);
	}

	return (
		<div data-cy={cy}>
			<Pills role="group" aria-label={groupLabel}>
				{conditionOpsFor(definition.kind).map(id => (
					<PillSlot key={id} selected={id === op}>
						<ActionButton
							data-cy={`${cy}-op-${id}`}
							size={ACTION_BUTTON_SIZE.COMPACT}
							aria-pressed={id === op}
							onClick={() => setOp(id)}
						>
							{conditionName(id)}
						</ActionButton>
					</PillSlot>
				))}
			</Pills>
			<Body onKeyDown={applyOnEnter}>
				<InputRow>
					{definition.kind === 'TEXT' && op !== 'empty' && (
						<Grow>{box('value', TEXT_PLACEHOLDER_IDS[op] as string, false)}</Grow>
					)}
					{isNumber && op === 'between' && (
						<>
							<NumberBox>{box('from', 'filters.custom_field.min', true)}</NumberBox>
							<And>{say({id: 'filters.custom_field.and'})}</And>
							<NumberBox>{box('to', 'filters.custom_field.max', true)}</NumberBox>
						</>
					)}
					{isNumber && op !== 'between' && op !== 'empty' && (
						<NumberBox>{box('value', 'filters.custom_field.value', true)}</NumberBox>
					)}
					{addButton}
				</InputRow>
				{definition.kind === 'TEXT' && op !== 'empty' && (
					<Helper>{say({id: 'filters.custom_field.text_helper'})}</Helper>
				)}
			</Body>
		</div>
	);
};
